import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ChevronLeft, Calendar, CheckCircle2, AlertCircle } from 'lucide-react';
import { apiGet, apiPost } from '../lib/api';
import { toast } from '../lib/toast';

interface Status {
  configured: boolean;
  connected: boolean;
  email?: string | null;
  calendar_id?: string;
  connected_at?: number | null;
}

const CALLBACK_MESSAGES: Record<string, { kind: 'success' | 'error'; text: string }> = {
  connected:        { kind: 'success', text: 'Google Calendar connected.' },
  denied:           { kind: 'error',   text: 'You declined the Google permission.' },
  invalid:          { kind: 'error',   text: 'Authorization request was invalid. Try again.' },
  expired:          { kind: 'error',   text: 'Authorization expired. Try again.' },
  exchange_failed:  { kind: 'error',   text: 'Google rejected the token exchange. Check that the OAuth redirect URI is registered.' },
  userinfo_failed:  { kind: 'error',   text: 'Could not fetch Google account info.' },
};

export function SettingsCalendar() {
  const [status, setStatus] = useState<Status | null>(null);
  const [busy, setBusy] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  async function load() {
    try {
      setStatus(await apiGet<Status>('/api/google/status'));
    } catch (e) {
      toast.error((e as Error).message);
    }
  }

  useEffect(() => {
    load();
    // Surface the redirect-back message once, then strip it from the URL so a
    // refresh doesn't re-fire the toast.
    const g = searchParams.get('google');
    if (g) {
      const msg = CALLBACK_MESSAGES[g];
      if (msg) (msg.kind === 'success' ? toast.success : toast.error)(msg.text);
      const sp = new URLSearchParams(searchParams);
      sp.delete('google');
      setSearchParams(sp, { replace: true });
    }
  }, []);

  async function connect() {
    setBusy(true);
    try {
      const r = await apiGet<{ url: string }>('/api/google/start');
      window.location.href = r.url;
    } catch (e) {
      toast.error((e as Error).message);
      setBusy(false);
    }
  }

  async function disconnect() {
    if (!confirm('Disconnect your Google Calendar? Existing mirrored events stay; new bookings will stop syncing.')) return;
    setBusy(true);
    try {
      await apiPost('/api/google/disconnect', {});
      toast.success('Disconnected.');
      await load();
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold tracking-tight">Google Calendar</h1>
        <Link to="/settings" className="text-sm text-muted inline-flex items-center gap-1">
          <ChevronLeft size={14} /> Settings
        </Link>
      </div>

      <div className="card text-sm text-muted">
        When connected, every booking you create or update is mirrored to your personal Google Calendar.
        Cancelled or no-show bookings are removed automatically. NGB never reads your existing events —
        sync is one-way (NGB → Google).
      </div>

      {!status ? (
        <div className="card text-muted">Loading…</div>
      ) : !status.configured ? (
        <div className="card border-yellow-500/30 bg-yellow-500/5">
          <div className="flex items-start gap-3">
            <AlertCircle size={20} className="text-yellow-300 shrink-0 mt-0.5" />
            <div className="text-sm">
              <div className="font-semibold text-yellow-200">Not configured on this instance</div>
              <div className="text-muted mt-1">
                Google Calendar integration needs OAuth credentials set as Cloudflare secrets
                (<code className="font-mono text-xs">GOOGLE_CLIENT_ID</code> and{' '}
                <code className="font-mono text-xs">GOOGLE_CLIENT_SECRET</code>). Ask your administrator.
              </div>
            </div>
          </div>
        </div>
      ) : status.connected ? (
        <div className="card space-y-3">
          <div className="flex items-start gap-3">
            <CheckCircle2 size={20} className="text-emerald-300 shrink-0 mt-0.5" />
            <div className="text-sm flex-1 min-w-0">
              <div className="font-semibold">Connected</div>
              <div className="text-muted font-mono truncate">{status.email}</div>
              {status.connected_at && (
                <div className="text-xs text-muted mt-1">
                  Since {new Date(status.connected_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                </div>
              )}
            </div>
          </div>
          <button
            onClick={disconnect}
            disabled={busy}
            className="btn-ghost w-full disabled:opacity-40"
          >
            {busy ? 'Working…' : 'Disconnect'}
          </button>
        </div>
      ) : (
        <button
          onClick={connect}
          disabled={busy}
          className="btn-primary w-full inline-flex items-center justify-center gap-2 disabled:opacity-40"
        >
          <Calendar size={16} />
          {busy ? 'Redirecting…' : 'Connect Google Calendar'}
        </button>
      )}
    </div>
  );
}
